import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SERVICE_OPTIONS } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your request has been received. We'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: form */}
          <div className="lg:col-span-7">
            <span className="text-sm text-gray-500 uppercase tracking-widest font-semibold">Get a Quote</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Tell us about your freight.
            </h2>
            <p className="mt-5 text-base text-gray-600 leading-relaxed max-w-xl">
              Send us your shipment details and a PrimeLane coordinator will follow up with a freight solution and pricing.
              No call centers — just direct communication.
            </p>

            <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="contact-form">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-[#001E52]">Name *</Label>
                <Input id="name" data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                  className="mt-2 rounded-none border-gray-300 focus-visible:ring-[#FE6A04]" placeholder="Full name" />
              </div>
              <div>
                <Label htmlFor="company" className="text-sm font-medium text-[#001E52]">Company</Label>
                <Input id="company" data-testid="contact-company" value={form.company} onChange={(e) => set("company", e.target.value)}
                  className="mt-2 rounded-none border-gray-300 focus-visible:ring-[#FE6A04]" placeholder="Company name" />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-[#001E52]">Email *</Label>
                <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => set("email", e.target.value)}
                  className="mt-2 rounded-none border-gray-300 focus-visible:ring-[#FE6A04]" placeholder="you@company.com" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-[#001E52]">Phone</Label>
                <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  className="mt-2 rounded-none border-gray-300 focus-visible:ring-[#FE6A04]" placeholder="(555) 000-0000" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm font-medium text-[#001E52]">Service of Interest</Label>
                <Select value={form.service} onValueChange={(v) => set("service", v)}>
                  <SelectTrigger data-testid="contact-service" className="mt-2 rounded-none border-gray-300 focus:ring-[#FE6A04]">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="message" className="text-sm font-medium text-[#001E52]">Message *</Label>
                <Textarea id="message" data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)}
                  rows={5} className="mt-2 rounded-none border-gray-300 focus-visible:ring-[#FE6A04]"
                  placeholder="Pickup, delivery, freight type, weight, volume, timeline, and any special requirements..." />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={loading} data-testid="contact-submit"
                  className="rounded-none bg-[#FE6A04] hover:bg-[#e15e02] text-white px-8 py-6 text-base font-semibold w-full sm:w-auto">
                  {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Send className="mr-2" size={18} />}
                  {loading ? "Sending…" : "Request a Quote"}
                </Button>
              </div>
            </form>
          </div>

          {/* Right: founder info */}
          <div className="lg:col-span-5">
            <div className="bg-[#001E52] p-8 lg:p-10 h-full" data-testid="founder-card">
              <div className="w-20 h-20 flex items-center justify-center bg-white/5 border border-white/10" data-testid="contact-company-icon">
                <Building2 size={36} className="text-[#FE6A04]" />
              </div>
              <p className="mt-6 text-[15px] text-slate-300 leading-relaxed">
                Reach out directly. You'll work with a dedicated coordinator focused on your freight from initial quote to final delivery.
              </p>
              <div className="mt-8 space-y-4 text-slate-200">
                <a href="mailto:info@primelanefreight.com" className="flex items-center gap-3 hover:text-white transition-colors" data-testid="founder-email">
                  <Mail size={18} className="text-[#FE6A04]" /> info@primelanefreight.com
                </a>
                <a href="tel:+17196466956" className="flex items-center gap-3 hover:text-white transition-colors" data-testid="founder-phone">
                  <Phone size={18} className="text-[#FE6A04]" /> (719) 646-6956
                </a>
                <div className="flex items-center gap-3">
                  <MapPin size={18} className="text-[#FE6A04]" /> Serving clients nationwide, USA
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
